import { ethers } from 'ethers';

import {
  connectWeb3Wallet,
  encodeNoticeIdBytes32,
  switchToSpokeNetwork,
} from './web3-escrow';

const HUB_ABI = [
  'function acceptTrade(bytes32 tradeId)',
  'function dispatchTrade(bytes32 tradeId, uint256 deliveryWindow)',
  'function claimDelegate(bytes32 tradeId)',
  'function settleTrade(bytes32 tradeId, string clearTextPin) payable',
  'function revokeDelegate(bytes32 tradeId)',
  'function getTrade(bytes32 tradeId) view returns (tuple(bytes32 tradeId, address initiator, address primaryPayee, address delegatePayee, uint256 baseAmount, uint256 bufferAmount, uint96 settlementBps, uint256 slaDeadline, uint8 state, bytes32 pinHash, uint32 sourceDomain))',
];

const UTT_SPOKE_ABI = [
  'function lockFunds(bytes32 tradeId, bytes32 pinHash, uint256 baseAmount, uint256 bufferAmount, uint96 settlementBps, uint32 hubDomain) payable',
  'event FundsLocked(bytes32 indexed tradeId, address indexed initiator, uint256 baseAmount, uint256 bufferAmount, uint96 settlementBps, bytes32 pinHash, uint32 hubDomain, bytes32 messageId)',
];

const DEFAULT_HUB_DOMAIN = 31337;
const DEFAULT_SETTLEMENT_BPS = '922337212060663816000';
const DEFAULT_DISPATCH_WINDOW_SEC = 3600;

declare global {
  interface Window {
    __HUB_ORDER_CONTRACT_ADDRESS__?: string;
    __UTT_SPOKE_CONTRACT_ADDRESS__?: string;
  }
}

const resolveHubOrderAddress = (): string => {
  const address =
    (typeof window !== 'undefined' && window.__HUB_ORDER_CONTRACT_ADDRESS__) ||
    process.env.REACT_APP_HUB_ORDER_CONTRACT_ADDRESS ||
    process.env.REACT_APP_HUB_CONTRACT_ADDRESS ||
    '';

  if (!address || !ethers.utils.isAddress(address)) {
    throw new Error('REACT_APP_HUB_ORDER_CONTRACT_ADDRESS is not configured');
  }

  return ethers.utils.getAddress(address);
};

const resolveUttSpokeAddress = (): string => {
  const address =
    (typeof window !== 'undefined' && window.__UTT_SPOKE_CONTRACT_ADDRESS__) ||
    process.env.REACT_APP_UTT_SPOKE_CONTRACT_ADDRESS ||
    process.env.REACT_APP_SPOKE_CONTRACT_ADDRESS ||
    '';

  if (!address || !ethers.utils.isAddress(address)) {
    throw new Error('REACT_APP_UTT_SPOKE_CONTRACT_ADDRESS is not configured');
  }

  return ethers.utils.getAddress(address);
};

const resolveHubDomain = (): number => {
  const configured = process.env.REACT_APP_HUB_DOMAIN || process.env.REACT_APP_HUB_CHAIN_ID;
  return configured ? Number(configured) : DEFAULT_HUB_DOMAIN;
};

export const isUniversalTradeEnabled = (): boolean =>
  Boolean(
    process.env.REACT_APP_HUB_ORDER_CONTRACT_ADDRESS ||
      process.env.REACT_APP_HUB_CONTRACT_ADDRESS ||
      (typeof window !== 'undefined' && window.__HUB_ORDER_CONTRACT_ADDRESS__)
  );

export const hashHandshakePin = (clearTextPin: string): string =>
  ethers.utils.keccak256(ethers.utils.toUtf8Bytes(clearTextPin));

export const getHubOrderContract = async (
  provider?: ethers.providers.Web3Provider
): Promise<ethers.Contract> => {
  const web3Provider = provider || (await connectWeb3Wallet());
  const signer = web3Provider.getSigner();
  return new ethers.Contract(resolveHubOrderAddress(), HUB_ABI, signer);
};

export const getUttSpokeContract = async (
  provider?: ethers.providers.Web3Provider
): Promise<ethers.Contract> => {
  await switchToSpokeNetwork();
  const web3Provider = provider || (await connectWeb3Wallet());
  const signer = web3Provider.getSigner();
  return new ethers.Contract(resolveUttSpokeAddress(), UTT_SPOKE_ABI, signer);
};

export const lockTradeFunds = async ({
  noticeId,
  baseAmountEth,
  bufferAmountEth = '0',
  clearTextPin,
  settlementBps = DEFAULT_SETTLEMENT_BPS,
}: {
  noticeId: string;
  baseAmountEth: string;
  bufferAmountEth?: string;
  clearTextPin: string;
  settlementBps?: string | number;
}): Promise<ethers.providers.TransactionReceipt> => {
  const contract = await getUttSpokeContract();
  const tradeId = encodeNoticeIdBytes32(noticeId);
  const pinHash = hashHandshakePin(clearTextPin);
  const baseAmount = ethers.utils.parseEther(baseAmountEth);
  const bufferAmount = ethers.utils.parseEther(bufferAmountEth);
  const hubDomain = resolveHubDomain();

  const tx = await contract.lockFunds(tradeId, pinHash, baseAmount, bufferAmount, ethers.BigNumber.from(settlementBps), hubDomain, {
    value: baseAmount.add(bufferAmount),
  });
  const receipt = await tx.wait();
  if (!receipt || receipt.status !== 1) {
    throw new Error('UTT lockFunds transaction failed');
  }
  return receipt;
};

export const acceptTrade = async (noticeId: string): Promise<ethers.providers.TransactionReceipt> => {
  const contract = await getHubOrderContract();
  const tradeId = encodeNoticeIdBytes32(noticeId);
  const tx = await contract.acceptTrade(tradeId);
  const receipt = await tx.wait();
  if (!receipt || receipt.status !== 1) {
    throw new Error('acceptTrade failed');
  }
  return receipt;
};

export const dispatchTrade = async (
  noticeId: string,
  deliveryWindowSec = DEFAULT_DISPATCH_WINDOW_SEC
): Promise<ethers.providers.TransactionReceipt> => {
  const contract = await getHubOrderContract();
  const tradeId = encodeNoticeIdBytes32(noticeId);
  const tx = await contract.dispatchTrade(tradeId, deliveryWindowSec);
  const receipt = await tx.wait();
  if (!receipt || receipt.status !== 1) {
    throw new Error('dispatchTrade failed');
  }
  return receipt;
};

export const claimDelegate = async (noticeId: string): Promise<ethers.providers.TransactionReceipt> => {
  const contract = await getHubOrderContract();
  const tradeId = encodeNoticeIdBytes32(noticeId);
  const tx = await contract.claimDelegate(tradeId);
  const receipt = await tx.wait();
  if (!receipt || receipt.status !== 1) {
    throw new Error('claimDelegate failed');
  }
  return receipt;
};

export const settleTrade = async (
  noticeId: string,
  clearTextPin: string
): Promise<ethers.providers.TransactionReceipt> => {
  const contract = await getHubOrderContract();
  const tradeId = encodeNoticeIdBytes32(noticeId);
  const tx = await contract.settleTrade(tradeId, clearTextPin, { value: 0 });
  const receipt = await tx.wait();
  if (!receipt || receipt.status !== 1) {
    throw new Error('settleTrade failed');
  }
  return receipt;
};

export const revokeDelegate = async (noticeId: string): Promise<ethers.providers.TransactionReceipt> => {
  const contract = await getHubOrderContract();
  const tradeId = encodeNoticeIdBytes32(noticeId);
  const tx = await contract.revokeDelegate(tradeId);
  const receipt = await tx.wait();
  if (!receipt || receipt.status !== 1) {
    throw new Error('revokeDelegate failed');
  }
  return receipt;
};
